import { Badge, Button, Card, CardContent, Skeleton, toast } from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import { CreditSparkline, type DailyBalance } from './credit-sparkline.js';

type Level = 'ok' | 'low' | 'out';

interface CreditStatus {
  key: 'gtisms' | 'apiplacas';
  label: string;
  configured: boolean;
  remaining: number | null;
  expiresOn: string | null;
  dailyAverage: number | null;
  daysLeft: number | null;
  level: Level | null;
  checkedAt: string | null;
  series: DailyBalance[];
}

interface CreditsResponse {
  providers: CreditStatus[];
}

const CREDITS_KEY = ['admin-credits'];

const UNITS: Record<CreditStatus['key'], string> = { gtisms: 'créditos', apiplacas: 'consultas' };

const LEVEL_CHIP: Record<Level, { label: string; variant: 'success' | 'warning' | 'danger' }> = {
  ok: { label: 'OK', variant: 'success' },
  low: { label: 'Baixo', variant: 'warning' },
  out: { label: 'Acabou', variant: 'danger' },
};

const formatDateTime = (raw: string) =>
  new Date(raw).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatDay = (isoDay: string) => isoDay.split('-').reverse().join('/');

const lasts = (days: number) =>
  days < 1 ? 'dura menos de 1 dia' : `dura cerca de ${days} ${days === 1 ? 'dia' : 'dias'}`;

const usageLine = (p: CreditStatus) => {
  const parts: string[] = [];
  if (p.dailyAverage && p.dailyAverage > 0) {
    parts.push(`${p.dailyAverage.toLocaleString('pt-BR')} por dia`);
    if (p.daysLeft !== null) parts.push(lasts(p.daysLeft));
  } else {
    parts.push('sem uso nos últimos 7 dias');
  }
  if (p.expiresOn) parts.push(`validade ${formatDay(p.expiresOn)}`);
  return parts.join(' · ');
};

const ProviderRow = ({ p }: { p: CreditStatus }) => {
  const chip = p.level ? LEVEL_CHIP[p.level] : null;
  return (
    <li className="flex flex-col gap-1 py-3" aria-label={p.label}>
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0 truncate text-sm font-medium">{p.label}</span>
        <Badge variant={chip?.variant ?? 'default'} className="shrink-0">
          {chip?.label ?? (p.configured ? 'Sem leitura' : 'Não configurado')}
        </Badge>
      </div>
      {p.remaining === null ? (
        <p className="text-xs text-muted-fg">
          {p.configured
            ? 'Ainda sem leitura. Clique em "Ler agora".'
            : 'Sem token configurado neste ambiente.'}
        </p>
      ) : (
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="text-lg font-semibold">
              {p.remaining.toLocaleString('pt-BR')} {UNITS[p.key]}
            </p>
            <p className="text-xs text-muted-fg">{usageLine(p)}</p>
          </div>
          <CreditSparkline series={p.series} unit={UNITS[p.key]} />
        </div>
      )}
    </li>
  );
};

/** SMS credits and API Placas lookups (#484), read every hour on the API side. */
export const CreditsCard = () => {
  const qc = useQueryClient();
  const credits = useQuery({
    queryKey: CREDITS_KEY,
    queryFn: () => api.request<CreditsResponse>('/v1/admin/credits'),
  });
  const readNow = useMutation({
    mutationFn: () => api.request<CreditsResponse>('/v1/admin/credits/run', { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData(CREDITS_KEY, data);
      toast.success('Saldos lidos agora.');
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível ler os saldos agora.'),
  });

  const providers = credits.data?.providers ?? [];
  const checkedAt = providers
    .map((p) => p.checkedAt)
    .filter((v): v is string => Boolean(v))
    .sort()
    .at(-1);

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold">Saldos</h2>
            <p className="text-xs text-muted-fg">
              {checkedAt ? `Última leitura: ${formatDateTime(checkedAt)}` : 'Ainda não lido.'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full shrink-0 sm:w-auto"
            disabled={readNow.isPending}
            onClick={() => readNow.mutate()}
          >
            {readNow.isPending ? 'Lendo...' : 'Ler agora'}
          </Button>
        </div>
        {credits.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : credits.isError ? (
          <p className="text-sm text-muted-fg">
            Não foi possível carregar. Tente recarregar a página.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {providers.map((p) => (
              <ProviderRow key={p.key} p={p} />
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-fg">
          Lido de hora em hora. O Pushover avisa quando o saldo não cobre 7 dias de uso, quando
          acaba, quando volta ao normal e quando os créditos de SMS estão perto de vencer.
        </p>
      </CardContent>
    </Card>
  );
};
